import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  withTextLocalizer,
  initJumioForWeb,
  selectJumioRedirectData,
  LocalizePropType,
  selectUser,
  isKYCApp,
  setNotification,
  APP,
  initJumioForMobile,
  selectBrandSettings,
  selectDeviceLanguage,
  getNextUserOpenChallengeRoute,
  selectNetworkActivities,
  CLIENT_TYPE,
  getUser,
  confirmHavingOldDl,
  showSchufaRequiredDialog,
} from 'getaway-data-layer';
import { isDashboardScreen, navigateTo, showAlert } from '../../../../../utils';
import DLOnboardingView from './DLOnboardingView';
import ExternalBrowser from '../../../../components/ExternalBrowser/ExternalBrowser';

type Props = {
  localize: LocalizePropType;
};

const DLOnboardingContainer = ({ localize }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const jumioRedirectData: any = useSelector(selectJumioRedirectData);
  const brandSettings = useSelector(selectBrandSettings);
  const user = useSelector(selectUser);
  const [showExternalBrowserScreen, setShowExternalBrowserScreen] =
    useState(false);
  const networkActivities: any = useSelector(selectNetworkActivities);
  const isLoading = networkActivities.some((activityType: any) =>
    [
      CLIENT_TYPE.USER_CLIENT.INIT_JUMIO_FOR_MOBILE,
      CLIENT_TYPE.USER_CLIENT.INIT_JUMIO_FOR_WEB,
    ].includes(activityType)
  );
  const language = useSelector(selectDeviceLanguage);
  const scanEnabled = user?.remainingLicenseSubmissionAttempts > 0;
  const [isPlasticCard, setIsPlasticCard] = useState(true);
  const [isDLOld, setIsDlOld] = useState<boolean>(undefined);

  const showResubmissionUI =
    ['deleted', 'rejected'].includes(user?.license?.licenseStatus) &&
    user?.license?.rejectedReason != null &&
    user?.license?.resubmitInstruction != null;

  const requireIDtoVerifyOldDL = Boolean(
    brandSettings.kycSettings?.requireIDtoVerifyOldDL &&
      brandSettings.kycSettings?.yearsToConsiderDLSelfieAsOld
  );

  const resubmitInstruction = user?.license?.resubmitInstruction;

  const navigateToJumioScan = (data) => {
    if (!data?.redirectUrl) return;

    const jumioUrl = new URL(data.redirectUrl);

    jumioUrl.searchParams.set(
      'locale',
      language === 'deformal' ? 'de' : language
    );
    window.location.href = jumioUrl.toString();
  };

  const navigateToScan = (url) => {
    const jumioUrl = new URL(url);

    jumioUrl.searchParams.set(
      'locale',
      language === 'deformal' ? 'de' : language
    );
    window.location.href = jumioUrl.toString();
  };

  const startJumio = (force: boolean = false, redirectUrl: string = '') => {
    if (scanEnabled && !isLoading) {
      if (brandSettings?.kycSettings?.jumioWebV4Enabled) {
        dispatch(
          initJumioForMobile(
            'DRIVING_LICENSE',
            (res) => {
              if (res?.web?.href) navigateToScan(res.web.href);
            },
            redirectUrl,
            redirectUrl,
            force,
            (error) => {
              if (
                Object.keys(error.detail.data.violations)?.includes(
                  'api_error.user.redirectToWeb'
                )
              ) {
                setShowExternalBrowserScreen(true);
              } else if (
                Object.keys(error.detail.data.violations)?.includes(
                  'api_error.user.cw_address_missing'
                )
              ) {
                showSchufaRequiredDialog(
                  localize,
                  () => navigateTo(history, '/dashboard/schufa'),
                  'profile.schufa.missing'
                );
              } else if (!force) {
                setShowExternalBrowserScreen(false);
              }
            }
          )
        );
      } else {
        dispatch(
          initJumioForWeb(
            localize,
            history,
            'DRIVING_LICENSE',
            jumioRedirectData?.transactionReference,
            user?.id,
            isDashboardScreen(),
            isKYCApp(),
            isKYCApp(),
            (data) => {
              navigateToJumioScan(data);
            },
            redirectUrl,
            redirectUrl,
            force
          )
        );
      }
    }
  };

  const onPressStart = (force: boolean = false) => {
    if (user?.blocked) {
      navigateTo(history, '/settings');
    } else if (
      (requireIDtoVerifyOldDL && isDLOld !== undefined && isDLOld) ||
      !isPlasticCard
    ) {
      dispatch(
        confirmHavingOldDl((updatedUser) => {
          const isLocalhost = window?.location?.hostname === 'localhost';
          const redirectUrl = isLocalhost
            ? null
            : `${window?.location?.origin}${getNextUserOpenChallengeRoute(
                updatedUser,
                'DLOnboarding'
              )}`;
          console.log('updatedUser', updatedUser);
          startJumio(force, redirectUrl);
        })
      );
    } else {
      const isLocalhost = window?.location?.hostname === 'localhost';
      const redirectUrl = isLocalhost
        ? null
        : `${window?.location?.origin}${getNextUserOpenChallengeRoute(
            user,
            'DLOnboarding'
          )}`;

      startJumio(force, redirectUrl);
    }
  };

  const onClickDisabled = () => {
    if (!isLoading) {
      dispatch(
        setNotification({
          message: 'user.scan.attempts.out',
          type: APP.NOTIFICATION_TYPE.ERROR,
        })
      );
    }
  };

  const onClose = () => {
    if (isDashboardScreen()) {
      navigateTo(history, '/settings');
    } else {
      showAlert(
        '',
        localize('cancel.FirstSteps.dialog.message'),
        localize('cancel.FirstSteps.dialog.yes'),
        () => navigateTo(history, '/settings'),
        localize('cancel.FirstSteps.dialog.no'),
        () => {},
        true
      );
    }
  };

  useEffect(() => {
    dispatch(getUser());
  }, []);

  return (
    <>
      {showExternalBrowserScreen ? (
        <ExternalBrowser
          localize={localize}
          onClose={onClose}
          onSkip={() => onPressStart(true)}
        />
      ) : (
        <DLOnboardingView
          user={user}
          isPlasticCard={isPlasticCard}
          setIsPlasticCard={setIsPlasticCard}
          localize={localize}
          scanEnabled={scanEnabled}
          onClose={onClose}
          onPressStart={() => onPressStart(false)}
          onClickDisabled={onClickDisabled}
          supportedCountries={[
            { text: localize('DE'), value: 'DE' },
            { text: localize('AT'), value: 'AT' },
            { value: 'other', text: localize('other') },
          ]}
          isLoading={isLoading}
          showResubmissionUI={showResubmissionUI}
          resubmitInstruction={resubmitInstruction}
          isDLOld={isDLOld}
          setIsDlOld={setIsDlOld}
          requireIDtoVerifyOldDL={requireIDtoVerifyOldDL}
          yearsToConsiderDLSelfieAsOld={
            brandSettings.kycSettings.yearsToConsiderDLSelfieAsOld
          }
          defaultCountry={brandSettings?.defaultCountryCode}
        />
      )}
    </>
  );
};

export default withTextLocalizer(DLOnboardingContainer);
